import { Col, Row } from 'antd';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EditOutlined, PictureOutlined, ZoomInOutlined } from '@ant-design/icons';
import { CURRENCY_FORMAT, UNIT_MEASURE } from '@common/constants';
import formatUOM from '@common/helpers/formatUOM';
import { setDefaultSrc } from '@common/utils';

import { invertColor } from '../../colors.utils';
import RoomvoButtonContainer from '../RoomvoButtonContainer';

import './ColorCard.scss';

interface ColorCardProps {
    discountPercentage?: number | null;
    hasVisualizer: boolean;
    imageSrc: string;
    onTrendLabel: string | undefined;
    isSelected: boolean;
    onClick?: () => void;
    onViewSlabClick?: () => void;
    overlayColor: string;
    price: number;
    priceWithoutDiscount: number;
    title: string;
    visualizerSku?: string;
}

const ColorCard = ({
    discountPercentage,
    hasVisualizer,
    imageSrc,
    onTrendLabel,
    isSelected,
    onClick,
    onViewSlabClick,
    overlayColor,
    price,
    priceWithoutDiscount,
    title,
    visualizerSku,
}: ColorCardProps): JSX.Element => {
    const { t } = useTranslation();
    const priceText = `${CURRENCY_FORMAT.format(price)} ${formatUOM(UNIT_MEASURE.MATERIALS)}`;

    const overlayInverseColor = useMemo(() => invertColor(overlayColor, false), [overlayColor]);
    const actionStyle = {
        color: overlayColor,
        filter: [
            `drop-shadow(1px 1px 0px ${overlayInverseColor})`,
            `drop-shadow(0px 0px 1px ${overlayInverseColor})`,
            `drop-shadow(0px 0px 1px ${overlayInverseColor})`,
        ].join(' '),
    };

    return (
        <div
            className={classNames('ColorCard multi-action-card', {
                'ColorCard--selected': isSelected,
            })}
            onClick={onClick}
        >
            <div className="ColorCard__image-container">
                {onTrendLabel && <div className="on-trend">{onTrendLabel}</div>}
                <img className="ColorCard__image" alt={title} src={imageSrc} onError={setDefaultSrc} />
                <Row className="ColorCard__actions-group">
                    <Col span={8}>
                        {isSelected && (
                            <div
                                className="ColorCard__action-container sub-action"
                                onClick={() => document.getElementById('fabric-canvas')?.scrollIntoView()}
                            >
                                <span className="ColorCard__action-text" style={actionStyle}>
                                    {t('SCROLLABLE_AREA.COLOR.BUTTONS.DESIGN')}
                                </span>
                                <EditOutlined className="ColorCard__action-icon" style={actionStyle} />
                            </div>
                        )}
                    </Col>
                    <Col span={8}>
                        {hasVisualizer && visualizerSku && (
                            // TODO: Remove this once Roomvo is correctly integrated integrated
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            <RoomvoButtonContainer sku={window.__mockRoomvoSku ?? visualizerSku}>
                                <div className="ColorCard__action-container sub-action">
                                    <span className="ColorCard__action-text" style={actionStyle}>
                                        {t('SCROLLABLE_AREA.COLOR.BUTTONS.VISUALIZE')}
                                    </span>
                                    <PictureOutlined className="ColorCard__action-icon" style={actionStyle} />
                                </div>
                            </RoomvoButtonContainer>
                        )}
                    </Col>
                    <Col span={8}>
                        <div className="ColorCard__action-container sub-action" onClick={onViewSlabClick}>
                            <span className="ColorCard__action-text" style={actionStyle}>
                                {t('SCROLLABLE_AREA.COLOR.BUTTONS.ZOOM')}
                            </span>
                            <ZoomInOutlined className="ColorCard__action-icon" style={actionStyle} />
                        </div>
                    </Col>
                </Row>
            </div>
            <div className="ColorCard__body">
                <span className="ColorCard__title">{title}</span>
                {discountPercentage ? (
                    <Row gutter={8} align="bottom">
                        <Col>
                            <img src={'./public/assets/images/Tags/sale-tag.svg'} alt="sale-tag" />
                        </Col>
                        <Col>
                            <span>{priceText}</span>
                            <div className="ColorCard__discount-line">
                                <span className="ColorCard__old-price">
                                    {CURRENCY_FORMAT.format(priceWithoutDiscount)}
                                </span>{' '}
                                <span className="ColorCard__discount-percentage">
                                    {t('PRICE.OFF', { value: discountPercentage })}
                                </span>
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <span>{priceText}</span>
                )}
            </div>
        </div>
    );
};

export default ColorCard;
